import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import Experience from '../Experience.js'

gsap.registerPlugin(ScrollTrigger)

/**
 * ScrollCamera
 * Bridges GSAP ScrollTrigger with the Three.js scene.
 *
 * Responsibilities:
 *  1. Hero text animations (on load)
 *  2. Camera position transitions on scroll
 *  3. Candle tilt in #pour section (rotation.z 0 → ~-110°)
 *  4. Section text reveals (fade-in, slide-up)
 *
 * Called from script.js AFTER resources.on('ready') so that
 * this.candle is guaranteed to exist on Experience.world.
 */
export default class ScrollCamera {
    constructor() {
        this.experience = new Experience()
        this.camera     = this.experience.camera
        this.world      = this.experience.world

        this.setupHeroEntrance()
        this.setupCandleTilt()
        this.setupTipsScroll()
        this.setupSectionReveals()
        this.setupCameraScroll()
    }

    // ── Hero: stagger text in on page load ───────────────────────
    setupHeroEntrance() {
        // Set hero title starting position for reveal-from-bottom effect
        gsap.set('.hero-title-line', { y: '110%' })

        // Smoke grow-in: стовпчик виростає знизу вгору після hero анімації
        const smokeProgress = [this.world.smoke, this.world.smoke2]
            .filter(Boolean)
            .map(s => s.material.uniforms.uProgress)
        gsap.to(smokeProgress, { value: 1, duration: 3.5, delay: 2.5, ease: 'power1.inOut' })

        const tl = gsap.timeline({ delay: 0.3 })

        tl.to('.hero-eyebrow', {
            opacity: 1, y: 0,
            duration: 0.9, ease: 'power3.out',
        })
        .to('.hero-title-line', {
            y: '0%',
            duration: 1.1, ease: 'expo.out',
        }, '-=0.5')
        .to('.hero-subtitle', {
            opacity: 1, y: 0,
            duration: 0.8, ease: 'power2.out',
        }, '-=0.7')
        .to('.hero-desc', {
            opacity: 1, y: 0,
            duration: 0.8, ease: 'power2.out',
        }, '-=0.5')
        .to('.scroll-hint', {
            opacity: 1,
            duration: 0.6, ease: 'power2.out',
        }, '-=0.3')
    }

    // ── #pour: pin + tilt candle on scroll ───────────────────────
    setupCandleTilt() {
        const candle  = this.world.candle
        const flame   = this.world.flame
        const flame2  = this.world.flame2
        const smoke   = this.world.smoke
        const smoke2  = this.world.smoke2
        if (!candle) return

        // Pin the #pour section (200vh tall) and tilt candle over its scroll range
        ScrollTrigger.create({
            trigger: '#pour',
            start: 'top top',
            end: 'bottom bottom',
            pin: false,
            onUpdate: (self) => {
                const p = self.progress    // 0 → 1

                // Tilt candle forward (X axis): 0 → ~+110° toward viewer
                candle.model.rotation.x = p * 1.92
                candle.model.rotation.z = 0

                // Rise candle upward — exits frame as oil pours down
                candle.model.position.y = -0.5 + p * 5.0

                // Lock idle animation for the whole scroll range
                candle.isPinned = p > 0.02

                // Flames fade out: p 0.0 → 0.15, then stay at 0
                const fadeOut = 1 - Math.min(Math.max(p / 0.15, 0), 1)
                if (flame)  flame.setFade(fadeOut)
                if (flame2) flame2.setFade(fadeOut)
                if (smoke)  smoke.setFade(fadeOut)
                if (smoke2) smoke2.setFade(fadeOut)
            },
            onLeave: () => {
                // Scrolled past pour — keep candle off-screen, flames off
                candle.model.position.y = 5.0
                candle.model.rotation.x = 1.92
                candle.isPinned = true
                if (flame)  flame.setFade(0)
                if (flame2) flame2.setFade(0)
                if (smoke)  smoke.setFade(0)
                if (smoke2) smoke2.setFade(0)
            },
            onEnterBack: () => {
                // onUpdate fires before this callback (GSAP ordering), so isPinned
                // was already set to true by onUpdate. Setting it false here causes
                // one idle-bob frame — keep it pinned so update() doesn't fight us.
                candle.isPinned = true
            },
            onLeaveBack: () => {
                // Scrolled back above pour — restore candle and flames
                candle.model.position.y = -0.5
                candle.model.rotation.x = 0
                candle.model.rotation.z = 0
                candle.isPinned = false
                if (flame)  flame.setFade(1)
                if (flame2) flame2.setFade(1)
                if (smoke)  smoke.setFade(1)
                if (smoke2) smoke2.setFade(1)
            },
        })

        // ── Pour text reveal
        gsap.to('.pour-heading', {
            scrollTrigger: {
                trigger: '#pour',
                start: 'top 80%',
                end: 'top 30%',
                scrub: 1,
            },
            opacity: 1, y: 0,
            ease: 'none',
        })
    }

    // ── #tips: horizontal scroll gallery (5 cards, 4 snaps) ────
    setupTipsScroll() {
        const track = document.querySelector('.tips-track')
        const grid  = document.querySelector('.tips-grid')
        if (!track || !grid) return

        // Distance = total card strip width minus one card (= track width)
        // Uses track.clientWidth so it respects the section's side padding
        const getDistance = () => grid.scrollWidth - track.clientWidth

        gsap.to(grid, {
            x: () => -getDistance(),
            ease: 'none',
            scrollTrigger: {
                trigger: '#tips',
                pin: true,
                scrub: 1,
                invalidateOnRefresh: true,
                start: 'top top',
                end: () => `+=${getDistance()}`,
                snap: {
                    snapTo: 1 / 4,   // 5 cards → 4 gaps
                    duration: { min: 0.2, max: 0.55 },
                    ease: 'power1.inOut',
                },
            },
        })
    }

    // ── Generic section reveals ────────────────────────────────
    setupSectionReveals() {
        // Section labels + titles: every .section-label and .section-title
        gsap.utils.toArray('.section-label').forEach((el) => {
            gsap.from(el, {
                scrollTrigger: { trigger: el, start: 'top 88%' },
                opacity: 0, y: 10,
                duration: 0.6, ease: 'power2.out',
            })
        })

        gsap.utils.toArray('.section-title').forEach((el) => {
            gsap.from(el, {
                scrollTrigger: { trigger: el, start: 'top 85%' },
                opacity: 0, y: 24,
                duration: 0.9, ease: 'power3.out',
            })
        })

        // About quote
        gsap.to('.about-quote', {
            scrollTrigger: { trigger: '.about-quote', start: 'top 80%' },
            opacity: 1, y: 0,
            duration: 1, ease: 'power2.out',
        })

        // Ingredient icons stagger
        gsap.to('.ingredient', {
            scrollTrigger: { trigger: '.ingredients-grid', start: 'top 85%' },
            opacity: 1, y: 0,
            stagger: 0.07,
            duration: 0.6, ease: 'power2.out',
        })

        // Tip cards: no stagger reveal — they appear via horizontal scroll
        // (tip-card has no opacity:0 initial state)

        // Category cards
        gsap.to('.category-card', {
            scrollTrigger: { trigger: '.categories-grid', start: 'top 80%' },
            opacity: 1, scale: 1,
            stagger: 0.12,
            duration: 0.7, ease: 'back.out(1.2)',
        })

        // Footer brand reveal
        gsap.from('.footer-logo', {
            scrollTrigger: { trigger: '#footer', start: 'top 85%' },
            opacity: 0, y: 32,
            duration: 1, ease: 'expo.out',
        })

        gsap.from('.cta-button', {
            scrollTrigger: { trigger: '#footer', start: 'top 80%' },
            opacity: 0, y: 16, scale: 0.95,
            duration: 0.8, ease: 'back.out(1.5)',
            delay: 0.3,
        })
    }

    // ── Camera subtle depth drift on global scroll ─────────────
    setupCameraScroll() {
        const cam = this.camera.instance

        // Gently pull camera back as user scrolls: Z 7 → 9
        ScrollTrigger.create({
            trigger: 'body',
            start: 'top top',
            end: 'bottom bottom',
            onUpdate: (self) => {
                const p = self.progress
                // Only apply outside the #pour pin zone (0.1 → 0.5 of total page)
                cam.position.z = 7 + p * 2
                // Slight upward camera drift to follow content
                cam.position.y = 1.2 - p * 0.5
            },
        })
    }
}
